// Typed API client. Wraps fetch with envelope unwrapping, error parsing,
// CSRF, and cookie-based session credentials.

import type {
  AppErrorBody,
  CollectionEnvelope,
  SingleEnvelope,
} from '@captain-tasko/shared';

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly current?: unknown;
  constructor(body: AppErrorBody, status: number) {
    super(body.error.message);
    this.code = body.error.code;
    this.status = status;
    this.details = body.error.details;
    this.current = body.current;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  ifMatch?: string;
  signal?: AbortSignal;
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = `/api/v1${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((item) => params.append(k, String(item)));
    else params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
  if (opts.method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(opts.method) && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;

  if (!res.ok) {
    if (parsed && parsed.error) throw new ApiError(parsed as AppErrorBody, res.status);
    throw new Error(`Request failed (${res.status})`);
  }
  return parsed as T;
}

export async function apiSingle<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const envelope = await apiFetch<SingleEnvelope<T>>(path, opts);
  return envelope.data;
}

export async function apiList<T>(path: string, opts: RequestOptions = {}): Promise<CollectionEnvelope<T>> {
  return apiFetch<CollectionEnvelope<T>>(path, opts);
}

/** CTSK-364 — PATCH one entry of a registry YAML file. Partial-entry
 *  merge semantics live server-side (see tools.ts: shallow top-level
 *  merge, external_refs per-tool deep-merge, null deletes optional
 *  fields, merged result re-validated against the file's schema).
 *  Returns the merged entry as persisted. */
export async function patchRegistryEntry<T = Record<string, unknown>>(
  toolId: string,
  filePath: string,
  entryId: string,
  patch: Record<string, unknown>,
): Promise<{ documentId: string; entryId: string; entry: T }> {
  return apiSingle(
    `/tools/${encodeURIComponent(toolId)}/registry/${encodeURIComponent(filePath)}/entries/${encodeURIComponent(entryId)}`,
    { method: 'PATCH', body: patch },
  );
}

/**
 * POST a single file as multipart/form-data.
 *
 * `fields` are extra text fields (CTSK-500: `ingestBatchId`). They are
 * appended BEFORE the file on purpose: FormData keeps insertion order and
 * @fastify/multipart only exposes fields that precede the file part, so a
 * field appended after the file would never reach the handler. Existing
 * callers pass no `fields` and send exactly what they sent before.
 */
export async function apiUpload<T>(path: string, file: File, fields?: Record<string, string>): Promise<T> {
  const form = new FormData();
  if (fields) for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  const headers: Record<string, string> = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(parsed as AppErrorBody, res.status);
  return (parsed as SingleEnvelope<T>).data;
}
